import { ALPHABET_WRITING_PLAYBACK_CONTRACT, } from './contracts.js';
export const AUTHORING_ENGINE_SCRIPT_ATTRIBUTE = 'data-alphabet-authoring-playback-engine';
export const AUTHORING_ENGINE_SCRIPT_HASH_ATTRIBUTE = 'data-alphabet-authoring-playback-engine-hash';
export const AUTHORING_ENGINE_SCRIPT_STATE_ATTRIBUTE = 'data-alphabet-authoring-playback-engine-state';
export const AUTHORING_ENGINE_SCRIPT_READY_ATTRIBUTE = 'data-alphabet-authoring-playback-engine-ready';
let loadedRendererSourceHash = null;
let playbackPromiseState = null;
export function isAuthoringPlaybackScriptLoaded(script) {
    const state = script.getAttribute(AUTHORING_ENGINE_SCRIPT_STATE_ATTRIBUTE);
    if (state === 'loaded')
        return true;
    if (state === 'loading' || state === 'failed')
        return false;
    return (script.getAttribute(AUTHORING_ENGINE_SCRIPT_READY_ATTRIBUTE) === 'true');
}
export function isAuthoringPlaybackScriptFailed(script) {
    return (script.getAttribute(AUTHORING_ENGINE_SCRIPT_STATE_ATTRIBUTE) === 'failed');
}
export function markAuthoringPlaybackScriptLoading(script) {
    script.setAttribute(AUTHORING_ENGINE_SCRIPT_STATE_ATTRIBUTE, 'loading');
    script.setAttribute(AUTHORING_ENGINE_SCRIPT_READY_ATTRIBUTE, 'false');
}
export function markAuthoringPlaybackScriptLoaded(script) {
    script.setAttribute(AUTHORING_ENGINE_SCRIPT_STATE_ATTRIBUTE, 'loaded');
    script.setAttribute(AUTHORING_ENGINE_SCRIPT_READY_ATTRIBUTE, 'true');
}
export function markAuthoringPlaybackScriptFailed(script) {
    script.setAttribute(AUTHORING_ENGINE_SCRIPT_STATE_ATTRIBUTE, 'failed');
    script.setAttribute(AUTHORING_ENGINE_SCRIPT_READY_ATTRIBUTE, 'false');
}
export function readAlphabetWritingPlayback(rendererVersion = null, rendererSourceHash = null) {
    const playback = globalThis.AlphabetAuthoringPlayback;
    if (playback?.playbackContract !== ALPHABET_WRITING_PLAYBACK_CONTRACT ||
        typeof playback.renderWordFrameToCanvas !== 'function' ||
        (rendererVersion && playback.version !== rendererVersion) ||
        (rendererSourceHash && loadedRendererSourceHash !== rendererSourceHash)) {
        return null;
    }
    return playback;
}
export function loadAlphabetWritingPlayback(options) {
    const existing = readAlphabetWritingPlayback(options.rendererVersion ?? null, options.rendererSourceHash);
    if (existing)
        return Promise.resolve(existing);
    if (playbackPromiseState?.rendererSourceHash === options.rendererSourceHash) {
        return playbackPromiseState.promise.then(() => requireLoadedPlayback(options));
    }
    const promise = loadPlayback(options);
    playbackPromiseState = {
        rendererSourceHash: options.rendererSourceHash,
        promise,
    };
    const clearPromiseState = () => {
        if (playbackPromiseState?.promise === promise) {
            playbackPromiseState = null;
        }
    };
    void promise.then(clearPromiseState, clearPromiseState);
    return promise.then(() => requireLoadedPlayback(options));
}
async function loadPlayback(options) {
    if (typeof document === 'undefined') {
        throw new Error('알파벳 따라쓰기 렌더러는 브라우저에서만 로드할 수 있습니다.');
    }
    removeStalePlaybackScripts(options.rendererSourceHash);
    if (options.embeddedRendererSource) {
        const previousPlayback = readGlobalPlayback();
        const previousRendererSourceHash = loadedRendererSourceHash;
        try {
            const attachEmbeddedPlayback = new Function(options.embeddedRendererSource);
            attachEmbeddedPlayback();
            loadedRendererSourceHash = options.rendererSourceHash;
            return requireLoadedPlaybackContract(options.rendererSourceHash);
        }
        catch (error) {
            restoreGlobalPlayback(previousPlayback);
            loadedRendererSourceHash = previousRendererSourceHash;
            throw error;
        }
    }
    const previousPlayback = readGlobalPlayback();
    const previousRendererSourceHash = loadedRendererSourceHash;
    return new Promise((resolve, reject) => {
        let existingScript = findPlaybackScript(options.rendererSourceHash);
        if (existingScript) {
            if (isAuthoringPlaybackScriptLoaded(existingScript)) {
                loadedRendererSourceHash = options.rendererSourceHash;
                const playback = readAlphabetWritingPlayback(null, options.rendererSourceHash);
                if (playback) {
                    resolve(playback);
                    return;
                }
                existingScript.remove();
                existingScript = null;
            }
            else if (isAuthoringPlaybackScriptFailed(existingScript)) {
                existingScript.remove();
                existingScript = null;
            }
        }
        const script = existingScript ?? document.createElement('script');
        const handleLoaded = () => {
            loadedRendererSourceHash = options.rendererSourceHash;
            try {
                const playback = requireLoadedPlaybackContract(options.rendererSourceHash);
                markAuthoringPlaybackScriptLoaded(script);
                resolve(playback);
            }
            catch (error) {
                markAuthoringPlaybackScriptFailed(script);
                script.remove();
                restoreGlobalPlayback(previousPlayback);
                loadedRendererSourceHash = previousRendererSourceHash;
                reject(error);
            }
        };
        script.addEventListener('load', handleLoaded, { once: true });
        script.addEventListener('error', () => {
            markAuthoringPlaybackScriptFailed(script);
            if (!existingScript)
                script.remove();
            reject(new Error('알파벳 따라쓰기 렌더러 요청 실패'));
        }, { once: true });
        if (existingScript)
            return;
        script.src = buildPlaybackScriptUrl(options);
        script.async = true;
        script.setAttribute(AUTHORING_ENGINE_SCRIPT_ATTRIBUTE, 'true');
        markAuthoringPlaybackScriptLoading(script);
        if (options.rendererSourceHash) {
            script.setAttribute(AUTHORING_ENGINE_SCRIPT_HASH_ATTRIBUTE, options.rendererSourceHash);
        }
        document.head.append(script);
    });
}
function requireLoadedPlayback(options) {
    const playback = readAlphabetWritingPlayback(options.rendererVersion ?? null, options.rendererSourceHash);
    if (!playback) {
        throw new Error('알파벳 따라쓰기 렌더러 계약이 일치하지 않습니다.');
    }
    return playback;
}
function requireLoadedPlaybackContract(rendererSourceHash) {
    const playback = readAlphabetWritingPlayback(null, rendererSourceHash);
    if (!playback) {
        throw new Error('알파벳 따라쓰기 렌더러 계약이 일치하지 않습니다.');
    }
    return playback;
}
function readGlobalPlayback() {
    return globalThis.AlphabetAuthoringPlayback;
}
function restoreGlobalPlayback(playback) {
    const scope = globalThis;
    if (playback) {
        scope.AlphabetAuthoringPlayback = playback;
    }
    else {
        delete scope.AlphabetAuthoringPlayback;
    }
}
function findPlaybackScript(rendererSourceHash) {
    const scripts = document.querySelectorAll(`script[${AUTHORING_ENGINE_SCRIPT_ATTRIBUTE}="true"]`);
    return ([...scripts].find((script) => (script.getAttribute(AUTHORING_ENGINE_SCRIPT_HASH_ATTRIBUTE) ??
        null) === rendererSourceHash) ?? null);
}
function removeStalePlaybackScripts(rendererSourceHash) {
    const scripts = document.querySelectorAll(`script[${AUTHORING_ENGINE_SCRIPT_ATTRIBUTE}="true"]`);
    for (const script of scripts) {
        const scriptHash = script.getAttribute(AUTHORING_ENGINE_SCRIPT_HASH_ATTRIBUTE) ?? null;
        if (scriptHash !== rendererSourceHash)
            script.remove();
    }
}
function buildPlaybackScriptUrl(options) {
    const assetPath = options.rendererAssetPath ?? options.fallbackAssetPath;
    if (/^https?:\/\//.test(assetPath))
        return assetPath;
    const resolvedPath = options.resolveAssetPath
        ? options.resolveAssetPath(assetPath)
        : assetPath;
    if (!options.rendererSourceHash || options.rendererAssetPath) {
        return resolvedPath;
    }
    const separator = resolvedPath.includes('?') ? '&' : '?';
    return `${resolvedPath}${separator}rendererSourceHash=${encodeURIComponent(options.rendererSourceHash)}`;
}
//# sourceMappingURL=playback-loader.js.map